import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createProductCategoryApi,
  createProductSubCategoryApi,
  createProductSubSubCategoryApi,
  deleteProductCategoryApi,
  deleteProductCategoryImageApi,
  getCompleteProductCategoriesApi,
  getProductChildCategoriesApi,
  getProductCategoriesApi,
  productCategoryFileUploadApi,
  updateProductCategoryApi,
  updateProductCategoryPriorityApi,
} from "../../api/ecommerce/ProductCategoryApi";

const initialState = {
  categories: {},
  completeCategories: {},
  childCategories: {},
  createResponse: {},
  createSubCategoryResponse: {},
  createSubSubCategoryResponse: {},
  updateResponse: {},
  deleteResponse: {},
  deleteImageResponse: {},
  deletingImage: false,
  deleteImageError: null,
  loading: false,
  completeCategoriesLoading: false,
  childCategoriesLoading: false,
  saving: false,
  priorityUpdating: false,
  deleting: false,
    combineFileUploading: false,
  fileuploadResponse: [],
};

export const getProductCategories = createAsyncThunk(
  "productCategory/getProductCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProductCategoriesApi();
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const createProductCategory = createAsyncThunk(
  "productCategory/createProductCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProductCategoryApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const createProductSubCategory = createAsyncThunk(
  "productCategory/createProductSubCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProductSubCategoryApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const createProductSubSubCategory = createAsyncThunk(
  "productCategory/createProductSubSubCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProductSubSubCategoryApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const getCompleteProductCategories = createAsyncThunk(
  "productCategory/getCompleteProductCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCompleteProductCategoriesApi();
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const getProductChildCategories = createAsyncThunk(
  "productCategory/getProductChildCategories",
  async (parentId, { rejectWithValue }) => {
    try {
      const response = await getProductChildCategoriesApi(parentId);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const updateProductCategory = createAsyncThunk(
  "productCategory/updateProductCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductCategoryApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);

export const updateProductCategoryPriority = createAsyncThunk(
  "productCategory/updateProductCategoryPriority",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductCategoryPriorityApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.response?.data?.errorMessage
          || error?.response?.data?.message || error?.message,
      });
    }
  }
);

export const deleteProductCategory = createAsyncThunk(
  "productCategory/deleteProductCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductCategoryApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  }
);


export const deleteProductCategoryImage = createAsyncThunk(
  "productCategory/deleteProductCategoryImage",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductCategoryImageApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.response?.data?.errorMessage
          || error?.response?.data?.message || error?.message,
      });
    }
  }
);

export const productCategoryFileUpload = createAsyncThunk(
  "productCategory/multipleImageUpload",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await productCategoryFileUploadApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

const productCategorySlice = createSlice({
  name: "productCategory",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(updateProductCategoryPriority.pending, (state) => {
      state.priorityUpdating = true;
    });
    builder.addCase(updateProductCategoryPriority.fulfilled, (state) => {
      state.priorityUpdating = false;
    });
    builder.addCase(updateProductCategoryPriority.rejected, (state) => {
      state.priorityUpdating = false;
    });
    builder.addCase(getProductCategories.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProductCategories.fulfilled, (state, action) => {
      state.categories = action?.payload;
      state.loading = false;
    });
    builder.addCase(getProductCategories.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(createProductCategory.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(createProductCategory.fulfilled, (state, action) => {
      state.createResponse = action?.payload;
      state.saving = false;
    });
    builder.addCase(createProductCategory.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(createProductSubCategory.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(createProductSubCategory.fulfilled, (state, action) => {
      state.createSubCategoryResponse = action?.payload;
      state.saving = false;
    });
    builder.addCase(createProductSubCategory.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(createProductSubSubCategory.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(createProductSubSubCategory.fulfilled, (state, action) => {
      state.createSubSubCategoryResponse = action?.payload;
      state.saving = false;
    });
    builder.addCase(createProductSubSubCategory.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(getCompleteProductCategories.pending, (state) => {
      state.completeCategoriesLoading = true;
    });
    builder.addCase(getCompleteProductCategories.fulfilled, (state, action) => {
      state.completeCategories = action?.payload;
      state.completeCategoriesLoading = false;
    });
    builder.addCase(getCompleteProductCategories.rejected, (state) => {
      state.completeCategoriesLoading = false;
    });

    builder.addCase(getProductChildCategories.pending, (state) => {
      state.childCategoriesLoading = true;
    });
    builder.addCase(getProductChildCategories.fulfilled, (state, action) => {
      state.childCategories = action?.payload;
      state.childCategoriesLoading = false;
    });
    builder.addCase(getProductChildCategories.rejected, (state) => {
      state.childCategoriesLoading = false;
    });

    builder.addCase(updateProductCategory.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(updateProductCategory.fulfilled, (state, action) => {
      state.updateResponse = action?.payload;
      state.saving = false;
    });
    builder.addCase(updateProductCategory.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(deleteProductCategory.pending, (state) => {
      state.deleting = true;
    });
    builder.addCase(deleteProductCategory.fulfilled, (state, action) => {
      state.deleteResponse = action?.payload;
      state.deleting = false;
    });
    builder.addCase(deleteProductCategory.rejected, (state) => {
      state.deleting = false;
    });

    builder.addCase(deleteProductCategoryImage.pending, (state) => {
      state.deletingImage = true;
      state.deleteImageError = null;
    });
    builder.addCase(deleteProductCategoryImage.fulfilled, (state, action) => {
      state.deletingImage = false;
      state.deleteImageResponse = action.payload;
    });
    builder.addCase(deleteProductCategoryImage.rejected, (state, action) => {
      state.deletingImage = false;
      state.deleteImageError = action.payload?.errorMessage || action.error?.message;
    });

      //website files
        builder.addCase(productCategoryFileUpload.pending, (state) => {
          state.combineFileUploading = true;
        });
        builder.addCase(productCategoryFileUpload.fulfilled, (state, action) => {
          state.fileuploadResponse = action.payload.response;
          state.combineFileUploading = false;
        });
        builder.addCase(productCategoryFileUpload.rejected, (state) => {
          state.combineFileUploading = false;
        });
  },
});

export default productCategorySlice.reducer;
